import { useEffect, useLayoutEffect, useState, type RefObject } from "react";
import { placeCentred } from "./metrics.ts";

/**
 * Measuring laid-out kit boxes in logical pixels, for a box that places another in its middle on whole
 * logical pixels once the browser has laid both out (a dialog in its scrim, a message line in its slot).
 */

/**
 * The logical pixel size of an element, from `--px` on it.
 *
 * @param element An element under the overlay root.
 * @returns CSS pixels per logical pixel, or null outside the overlay.
 */
export function cssPerLogical(element: HTMLElement): number | null {
  const px = Number.parseFloat(getComputedStyle(element).getPropertyValue("--px"));
  return Number.isFinite(px) && px > 0 ? px : null;
}

/**
 * A box's place in the middle of an area, and whether it has one: a box is placed once the area and the box are
 * both laid out, with a size, under the overlay root.
 */
export interface Centred {
  readonly left: number;
  readonly top: number;
  readonly placed: boolean;
}

/**
 * Where a box sits in the middle of an area on whole logical pixels, from their laid-out sizes, or null when they
 * cannot be measured: outside the overlay (no `--px`), or while the area has no size on an axis the box is centred
 * on, as under a hidden host, where every size reads 0 and a place worked out from them would be the area's top
 * left.
 *
 * @param area The area's size in CSS pixels.
 * @param box The box's size in CSS pixels.
 * @param px CSS pixels per logical pixel, or null outside the overlay.
 * @param axes Which axes to centre on.
 * @returns The box's offsets from the area's top left in logical pixels, or null.
 * @example
 * centredPlace({ width: 640, height: 360 }, { width: 200, height: 100 }, 2, { x: true, y: true });
 * // { left: 110, top: 65 }
 */
export function centredPlace(area: { readonly width: number; readonly height: number }, box: { readonly width: number; readonly height: number }, px: number | null, axes: { readonly x: boolean; readonly y: boolean }): { readonly left: number; readonly top: number } | null {
  if (px === null || (axes.x && area.width <= 0) || (axes.y && area.height <= 0)) {
    return null;
  }
  return { left: axes.x ? placeCentred(area.width / px, box.width / px) : 0, top: axes.y ? placeCentred(area.height / px, box.height / px) : 0 };
}

/**
 * Where a box sits in the middle of an area, on whole logical pixels, kept up to date as either changes
 * size. Until the area is laid out with a size, and outside the overlay, the box sits at the area's top left and is
 * not `placed`; a consumer that must never show the box there, such as the loading screen, whose host is hidden
 * while character select stands in front and shown again by a resize the observer reports only after a paint,
 * keeps the box hidden until it is placed. A box whose area loses its size (its host hidden) is no longer placed.
 *
 * @param area The area.
 * @param box The box, or a ref that holds null while there is none.
 * @param axes Which axes to centre on.
 * @param key A value that changes when the box is replaced or its content changes, so the observer follows
 *   the new box.
 * @returns The box's offsets from the area's top left in logical pixels, and whether they are a placement.
 */
export function useCentred(area: RefObject<HTMLElement | null>, box: RefObject<HTMLElement | null>, axes: { readonly x: boolean; readonly y: boolean }, key: unknown): Centred {
  const [place, setPlace] = useState<Centred>({ left: 0, top: 0, placed: false });
  useLayoutEffect(() => {
    const outer = area.current;
    const inner = box.current;
    if (outer === null || inner === null) {
      return undefined;
    }

    /**
     * Places the box in the middle of the area on whole logical pixels, or marks it unplaced, where it was, while
     * either has no size.
     */
    const centre = (): void => {
      const next = centredPlace(outer.getBoundingClientRect(), inner.getBoundingClientRect(), cssPerLogical(outer), axes);
      setPlace((current) => {
        if (next === null) {
          return current.placed ? { ...current, placed: false } : current;
        }
        return current.placed && current.left === next.left && current.top === next.top ? current : { ...next, placed: true };
      });
    };
    centre();
    const observer = new ResizeObserver(centre);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [area, box, axes.x, axes.y, key]);
  return place;
}

/**
 * An element's laid-out size in logical pixels, kept up to date as it changes size, for a layout that depends on
 * how much room a box has or takes (the loading screen's parts on a short area).
 *
 * @param box The element, or a ref that holds null while there is none.
 * @param key A value that changes when the element is replaced, so the observer follows the new one.
 * @returns Its width and height in logical pixels, fractions kept, or null while there is no element, outside the
 *   overlay (no `--px`), and while it has no size, as under a hidden host.
 */
export function useLogicalSize(box: RefObject<HTMLElement | null>, key: unknown): { readonly width: number; readonly height: number } | null {
  const [size, setSize] = useState<{ readonly width: number; readonly height: number } | null>(null);
  useLayoutEffect(() => {
    const element = box.current;
    if (element === null) {
      setSize(null);
      return undefined;
    }

    /**
     * Takes the element's size, or none while it cannot be measured.
     */
    const measure = (): void => {
      const px = cssPerLogical(element);
      const rect = element.getBoundingClientRect();
      const next = px === null || rect.width <= 0 || rect.height <= 0 ? null : { width: rect.width / px, height: rect.height / px };
      setSize((current) => (current?.width === next?.width && current?.height === next?.height ? current : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [box, key]);
  return size;
}

/**
 * How much of a logical pixel a measured width may exceed a whole number by before the next whole width is taken, so a
 * width the browser reports a hair over a whole number does not grow a box by a pixel.
 */
const WHOLE_TOLERANCE = 0.01;

/**
 * The whole number of logical pixels an element's natural width rounds up to, for a box whose width follows its text
 * (a menu, a legend's word) and so would lie half a pixel off the grid. The element is laid out with no width the first
 * time its `key` is seen and again after the pixel font has loaded; the result is its measured width rounded up to a
 * whole logical pixel, which the caller then sets as the element's width. The key is what the natural width depends on:
 * a new key drops the width, so the element is measured at its new natural width.
 *
 * @param box The element, or a ref that holds null while there is none.
 * @param key A value that changes whenever the element's natural width may.
 * @param enabled False where the caller sets its own width, which this then leaves alone.
 * @returns The width in logical pixels to set, or null while there is none (not yet measured, outside the overlay, or
 *   with no size).
 */
export function useWholeWidth(box: RefObject<HTMLElement | null>, key: string, enabled: boolean = true): number | null {
  const [whole, setWhole] = useState<{ readonly key: string; readonly width: number } | null>(null);
  useLayoutEffect(() => {
    const element = box.current;
    if (!enabled || element === null || whole?.key === key) {
      return;
    }
    const px = cssPerLogical(element);
    const measured = element.getBoundingClientRect().width;
    if (px !== null && measured > 0) {
      setWhole({ key, width: Math.ceil(measured / px - WHOLE_TOLERANCE) });
    }
  }, [box, key, enabled, whole]);
  useEffect(() => {
    if (!enabled || typeof document === "undefined" || document.fonts === undefined) {
      return undefined;
    }
    const again = (): void => setWhole(null);
    document.fonts.addEventListener("loadingdone", again);
    return () => document.fonts.removeEventListener("loadingdone", again);
  }, [enabled]);
  return enabled && whole?.key === key ? whole.width : null;
}
